import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { Input, Stack, Typography } from '@lyearn/ui';
import { RendererParams } from '@/components/Form';
import NumberInput from '@/components/NumberInput';
import Duration from '@/modules/corporate/components/Duration';
import MetricToogleGroup from '@/modules/goals/modules/okr/views/OKREditor/components/KeyResultForm/components/MetricToggleGroup';
import { KeyResultMetricEnum } from '@/types/schema';

const ALLOWED_METRICS = [
  KeyResultMetricEnum.Toggle,
  KeyResultMetricEnum.Number,
  KeyResultMetricEnum.Time,
];

type Props = {
  metric: KeyResultMetricEnum;
  onChangeMetric: (metric: KeyResultMetricEnum) => void;
  targetValueKey?: string;
  targetValueUnitKey?: string;
  isEdit?: boolean;
  error?: React.ReactNode;
  targetValue: number;
  targetValueUnit: string;
} & Pick<RendererParams, 'setFieldValue'>;

const HabitMetricGroup = (props: Props) => {
  const { t } = useTranslation();
  const {
    metric,
    onChangeMetric: _onChangeMetric,
    setFieldValue,
    targetValueUnitKey,
    targetValueKey,
    isEdit: disabled,
    error,
    targetValue,
    targetValueUnit,
  } = props;

  const onChangeTargetValue = useCallback(
    (targetValue?: number) => {
      targetValueKey && setFieldValue(targetValueKey, targetValue);
    },
    [setFieldValue, targetValueKey],
  );

  const onChangeTargetValueUnit = useCallback(
    (targetValueUnit?: string) => {
      targetValueUnitKey && setFieldValue(targetValueUnitKey, targetValueUnit);
    },
    [setFieldValue, targetValueUnitKey],
  );

  const onChangeMetric = useCallback(
    (updatedMetric: KeyResultMetricEnum) => {
      onChangeTargetValue(undefined);
      onChangeTargetValueUnit(undefined);
      _onChangeMetric(updatedMetric);
    },
    [_onChangeMetric, onChangeTargetValue, onChangeTargetValueUnit],
  );

  return (
    <Stack direction="col" gapY={32}>
      <MetricToogleGroup
        disabled={disabled}
        metrics={ALLOWED_METRICS}
        size="large"
        value={metric}
        onChange={onChangeMetric}
      />
      {metric === KeyResultMetricEnum.Time || metric === KeyResultMetricEnum.Number ? (
        <Stack direction="col" gapY={8}>
          <Typography className="ps-8" variant="body-short-02">
            {t('Habits:Editor.GoalPerDay')}
          </Typography>
          {metric === KeyResultMetricEnum.Time ? (
            <Duration
              className="flex flex-1"
              error={!!error}
              numberInputStyles="flex flex-1"
              value={targetValue}
              onChange={onChangeTargetValue}
            />
          ) : null}
          {metric === KeyResultMetricEnum.Number ? (
            <Stack className="flex flex-1 space-s-12">
              <NumberInput
                canBeEmpty
                className="flex flex-1"
                error={!!error}
                min={0}
                value={targetValue}
                onChange={onChangeTargetValue}
              />
              <Input
                className="flex flex-1"
                placeholder={t('Habits:Editor.UnitPlaceholder')}
                size="large"
                value={targetValueUnit}
                onChangeText={onChangeTargetValueUnit}
              />
            </Stack>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
};

export default HabitMetricGroup;
